import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaCheck, FaTimes, FaEdit, FaMicrophone, FaCalendarAlt, FaMapMarkerAlt, FaClock, FaUser, FaBuilding } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Helper to format date as dd/mm/yy
function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

// Avatar helpers
const AVATAR_COLORS = [
  "from-green-500 to-teal-600",
  "from-violet-500 to-purple-700",
  "from-blue-500 to-indigo-700",
  "from-amber-500 to-orange-600",
  "from-rose-500 to-pink-700",
  "from-cyan-500 to-sky-700",
];
function getInitials(name) {
  if (!name) return "P";
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    : name[0].toUpperCase();
}
function getAvatarColor(id) {
  if (!id) return AVATAR_COLORS[0];
  return AVATAR_COLORS[id.charCodeAt(id.length - 1) % AVATAR_COLORS.length];
}

const PendingPaidConference = () => {
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedConference, setSelectedConference] = useState(null);
  const [modal, setModal] = useState({ open: false, action: null, conferenceId: null });
  const [rejectReason, setRejectReason] = useState("");
  const [modifyNote, setModifyNote] = useState("");
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedReporters, setSelectedReporters] = useState([]);
  const [allReporters, setAllReporters] = useState([]);
  const navigate = useNavigate();
  const [refetchTrigger, setRefetchTrigger] = useState(false);
  const [showApproveSpinner, setShowApproveSpinner] = useState(false);
  const [showRejectSpinner, setShowRejectSpinner] = useState(false);
  const [showModifySpinner, setShowModifySpinner] = useState(false);

  // Fetch all reporters
  useEffect(() => {
    const fetchReporters = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/get/all/reporter`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setAllReporters(response.data.reporters || []);
      } catch (error) {
        console.error("Failed to fetch reporters:", error);
        setAllReporters([]);
      }
    };
    fetchReporters();
  }, []);

  useEffect(() => {
    const fetchConferences = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/all`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("Fetched all Paid Conferences Data:", response.data);
        const conferencesData = response.data.data?.conferences || response.data.conferences || [];
        setConferences(conferencesData);
      } catch (err) {
        setError("Failed to fetch paid conferences");
      } finally {
        setLoading(false);
      }
    };
    fetchConferences();
  }, [refetchTrigger]);

  // Search/filter states
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 15;
  const [todayOnly, setTodayOnly] = useState(false);

  // Set startDate and endDate to today when todayOnly is checked
  useEffect(() => {
    if (todayOnly) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayStr = `${yyyy}-${mm}-${dd}`;
      setStartDate(todayStr);
      setEndDate(todayStr);
    }
  }, [todayOnly]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, startDate, endDate, todayOnly]);


  // Optionally set to end of day if endOfDay=true
  const toDate = (dateStr, endOfDay = false) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      return null;
    }
    if (endOfDay) {
      d.setHours(23, 59, 59, 999);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    return d;
  };


  const filteredConferences = conferences.filter((conference) => {
    if (conference.status !== "pending") return false;

    // Convert conference dates to Date objects
    const conferenceDate = toDate(conference.conferenceDate);
    const filterStart = toDate(startDate);
    const filterEnd = toDate(endDate, true);

    // Skip conferences with invalid dates if filters are active
    if ((todayOnly || filterStart || filterEnd) && !conferenceDate) {
      return false;
    }

    if (todayOnly) {
      const today = toDate(new Date());
      if (!conferenceDate || conferenceDate.getTime() !== today.getTime()) {
        return false;
      }
    } else if (filterStart && filterEnd) {
      if (!conferenceDate || conferenceDate < filterStart || conferenceDate > filterEnd) {
        return false;
      }
    } else if (filterStart) {
      if (!conferenceDate || conferenceDate < filterStart) {
        return false;
      }
    } else if (filterEnd) {
      if (!conferenceDate || conferenceDate > filterEnd) {
        return false;
      }
    }

    // Search filter
    if (searchText) {
      const s = searchText.toLowerCase();
      const idMatch = conference.conferenceId && conference.conferenceId.toLowerCase().includes(s);
      const stateMatch = conference.state && conference.state.toLowerCase().includes(s);
      const cityMatch = conference.city && conference.city.toLowerCase().includes(s);

      if (!idMatch && !stateMatch && !cityMatch) return false;
    }

    return true;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredConferences.length / rowsPerPage);
  const paginatedConferences = filteredConferences.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };


  // Handle approve conference
  const handleApprove = async (conferenceId) => {
    setShowApproveSpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/${conferenceId}/action`,
        {
          action: "approved",
          note: "Paid conference approved by admin",
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Approve conference Response:", response.data);
      if (response.data && response.data.success) {
        toast.success("Paid conference approved successfully");
        setTimeout(() => {
          setShowApproveSpinner(false);
          window.location.reload();
        }, 1500);
      } else {
        setShowApproveSpinner(false);
        toast.error(
          (response.data && response.data.message) ||
          "Failed to approve paid conference"
        );
      }
    } catch (error) {
      setShowApproveSpinner(false);
      toast.error("Failed to approve paid conference");
    }
  };

  // Handle reject conference
  const handleReject = async (conferenceId, reason) => {
    console.log("Rejecting conference:", conferenceId, "Reason:", reason);
    setShowRejectSpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/${conferenceId}/action`,
        {
          action: "rejected",
          note: reason
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Reject Response:", response.data);
      if (response.data.success) {
        toast.success(
          response.data.message || "Paid conference rejected successfully"
        );
        setTimeout(() => {
          setShowRejectSpinner(false);
          window.location.reload();
        }, 1500);
      } else {
        setShowRejectSpinner(false);
        toast.error(response.data.message || "Failed to reject paid conference");
      }
    } catch (error) {
      setShowRejectSpinner(false);
      console.error("Error rejecting conference:", error);
      toast.error("Failed to reject paid conference");
    }
  };

  // Handle modify conference
  const handleModify = async (conferenceId) => {
    setShowModifySpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/${conferenceId}/action`,
        {
          action: "modified",
          note: modifyNote,
          selectedStates: selectedStates,
          adminSelectCities: selectedCities,
          reporterId: selectedReporters,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Modify Response:", response.data);
      if (response.data.success) {
        toast.success("Paid conference modified successfully");
        setTimeout(() => {
          setShowModifySpinner(false);
          window.location.reload();
        }, 1500);
      } else {
        setShowModifySpinner(false);
        toast.error(response.data.message || "Failed to modify paid conference");
      }
    } catch (error) {
      setShowModifySpinner(false);
      console.error("Error modifying conference:", error);
      toast.error("Failed to modify paid conference");
    }
  };

  // Modal confirm handler
  const handleModalConfirm = () => {
    if (modal.action === "approve") {
      handleApprove(modal.conferenceId);
    } else if (modal.action === "reject") {
      if (!rejectReason.trim()) {
        toast.error("Please enter a reason for rejection");
        return;
      }
      handleReject(modal.conferenceId, rejectReason);
    } else if (modal.action === "modify") {
      if (!modifyNote.trim()) {
        toast.error("Please enter a modification note");
        return;
      }
      handleModify(modal.conferenceId);
    }
    setModal({ open: false, action: null, conferenceId: null });
    setRejectReason("");
    setModifyNote("");
    setSelectedStates([]);
    setSelectedCities([]);
    setSelectedReporters([]);
  };

  // Modal cancel handler
  const handleModalCancel = () => {
    setModal({ open: false, action: null, conferenceId: null });
    setRejectReason("");
    setModifyNote("");
    setSelectedStates([]);
    setSelectedCities([]);
    setSelectedReporters([]);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <>
      {/* Search/Filter Bar OUTSIDE page, at the very top */}
      <div className="w-full sticky top-0 z-30 bg-gradient-to-r from-blue-100 to-blue-50 shadow-lg border-b mb-8 py-4 px-3 sm:px-8 flex flex-col gap-2">
        <div className="w-full max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-stretch sm:items-center bg-white rounded-xl shadow-md px-3 sm:px-5 py-4 border border-blue-200 w-full backdrop-blur-sm bg-white/90">

              {/* Date Range Filter - improved responsive layout */}
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full">
                  <div className="flex flex-row items-center gap-2 w-full sm:w-auto">
                    <span className="text-blue-600 font-semibold text-sm whitespace-nowrap flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      Start
                    </span>
                    <input
                      type="date"
                      className="border border-blue-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition w-full sm:w-auto min-w-[130px] shadow-sm"
                      value={startDate}
                      onChange={e => {
                        setStartDate(e.target.value);
                        setTodayOnly(false);
                        console.log("Start Date Set:", e.target.value);
                      }}
                      max={endDate || undefined}
                      aria-label="Start Date"
                    />
                  </div>

                  <div className="flex flex-row items-center gap-2 w-full sm:w-auto">
                    <span className="mx-1 text-gray-400 font-bold hidden sm:block">→</span>
                    <span className="text-blue-600 font-semibold text-sm whitespace-nowrap flex items-center">
                      <svg className="w-4 h-4 mr-1 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      End
                    </span>
                    <input
                      type="date"
                      className="border border-blue-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition w-full sm:w-auto min-w-[130px] shadow-sm"
                      value={endDate}
                      onChange={e => {
                        setEndDate(e.target.value);
                        setTodayOnly(false);
                        console.log("End Date Set:", e.target.value);
                      }}
                      min={startDate || undefined}
                      aria-label="End Date"
                    />
                  </div>
                </div>
              </div>

              {/* Today checkbox - improved styling */}
              <label className="flex items-center gap-2 text-blue-600 font-medium text-sm cursor-pointer ml-0 sm:ml-2 w-full sm:w-auto bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors">
                <input
                  type="checkbox"
                  className="accent-blue-500 w-4 h-4"
                  checked={todayOnly}
                  onChange={e => {
                    setTodayOnly(e.target.checked);
                    if (e.target.checked) {
                      setStartDate("");
                      setEndDate("");
                    }
                  }}
                />
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  Today
                </span>
              </label>

              {/* Search bar - improved styling */}
              <div className="flex items-center gap-2 w-full sm:w-auto relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </div>
                <input
                  type="text"
                  className="border border-blue-200 rounded-lg py-1.5 pl-10 pr-3 text-sm w-full sm:w-60 focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition shadow-sm"
                  placeholder="Search by Conference ID, State, or City"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  aria-label="Search by Conference ID, State, or City"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Main page content below */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
          Pending Paid Conferences
        </h2>
        <div className="flex flex-wrap gap-2 sm:space-x-2 sm:space-y-0">
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-green-500 text-white rounded hover:bg-green-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/press-conference/paid-conference/approved")}
          >
            Approved
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-orange-500 text-white rounded hover:bg-orange-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/press-conference/paid-conference/modified")}
          >
            Modified
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-red-500 text-white rounded hover:bg-red-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/press-conference/paid-conference/rejected")}
          >
            Rejected
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-blue-500 text-white rounded hover:bg-blue-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/press-conference/paid-conference/running")}
          >
            Running
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-purple-500 text-white rounded hover:bg-purple-600 font-semibold transition-colors text-sm sm:text-base"
            onClick={() => navigate("/admin/press-conference/paid-conference?status=completed")}
          >
            Completed
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-xl shadow-sm border border-gray-100">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-black text-gray-500 uppercase tracking-wider">Conference ID</th>
              <th className="px-4 py-3 text-left text-xs font-black text-gray-500 uppercase tracking-wider">Created By</th>
              <th className="px-4 py-3 text-left text-xs font-black text-gray-500 uppercase tracking-wider">Date &amp; Time</th>
              <th className="px-4 py-3 text-left text-xs font-black text-gray-500 uppercase tracking-wider">Location</th>
              <th className="px-4 py-3 text-left text-xs font-black text-gray-500 uppercase tracking-wider">Created At</th>
              <th className="px-4 py-3 text-left text-xs font-black text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-black text-gray-500 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedConferences.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-4">
                  No pending paid conferences found.
                </td>
              </tr>
            ) : (
              paginatedConferences.map((conference) => (
                <tr key={conference._id} className="border-t hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-sm text-gray-700">{conference.conferenceId}</td>

                  {/* -------- Created By -------- */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getAvatarColor(conference.submittedBy?._id || conference.submittedBy?.pressConferenceId)} flex items-center justify-center text-white text-sm font-black shadow-md flex-shrink-0`}
                        title={conference.submittedBy?.name || "Unknown"}
                      >
                        {getInitials(conference.submittedBy?.name)}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800 leading-tight">
                          {conference.submittedBy?.name || "N/A"}
                        </p>
                        <p className="text-xs text-gray-400 font-medium leading-tight">
                          {conference.submittedBy?.organization || ""}
                        </p>
                        {conference.submittedBy?.pressConferenceId && (
                          <span className="inline-block mt-0.5 px-1.5 py-0.5 bg-yellow-50 text-yellow-700 text-[9px] font-black uppercase tracking-wider rounded border border-yellow-100">
                            {conference.submittedBy.pressConferenceId}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  {/* -------- /Created By -------- */}

                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm">
                      <FaCalendarAlt className="mr-1 text-blue-500" />
                      {conference.conferenceDate}
                    </div>
                    <div className="flex items-center text-xs text-gray-500">
                      <FaClock className="mr-1" />
                      {conference.conferenceTime} {conference.timePeriod}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm">
                      <FaMapMarkerAlt className="mr-1 text-green-500" />
                      {conference.city}, {conference.state}
                    </div>
                    <div className="text-xs text-gray-500">{conference.place}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {new Date(conference.submittedAt).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                    <br />
                    {new Date(conference.submittedAt).toLocaleTimeString('en-IN', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${conference.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : conference.status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                        }`}
                    >
                      {conference.status === "pending" ? "Under Review" : conference.status}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <button
                      className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                      onClick={() => setSelectedConference(conference)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-4 sm:space-y-0">
          {/* Page Info */}
          <div className="text-sm text-gray-600">
            Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, filteredConferences.length)} of {filteredConferences.length} conferences
          </div>

          {/* Pagination Navigation */}
          <nav className="inline-flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded border border-blue-400 text-blue-800 bg-white hover:bg-blue-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Previous
            </button>

            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 rounded border transition-colors ${currentPage === pageNum
                      ? 'border-blue-400 bg-blue-300 text-blue-900 font-bold'
                      : 'border-blue-400 text-blue-800 bg-white hover:bg-blue-100'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded border border-blue-400 text-blue-800 bg-white hover:bg-blue-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Next
            </button>
          </nav>
        </div>
      )}


      {/* Popup Modal for Conference Details and Actions */}
      {selectedConference && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 transition-all">
          <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-full max-w-xs sm:max-w-md md:max-w-2xl text-left relative animate-fadeIn border border-blue-200 overflow-y-auto max-h-[90vh]">
            {/* Close button */}
            <button
              className="absolute top-2 right-3 text-gray-400 hover:text-gray-700 text-3xl font-bold"
              onClick={() => setSelectedConference(null)}
              aria-label="Close"
            >
              &times;
            </button>

            {/* Header with title and status - centered */}
            <div className="text-center mb-6">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-2">
                {selectedConference.topic || "Conference Details"}
              </h2>
              <span
                className={`inline-block px-4 py-1 rounded-full text-xs sm:text-sm font-semibold ${selectedConference.status === "approved"
                  ? "bg-green-100 text-green-800"
                  : selectedConference.status === "rejected"
                    ? "bg-red-100 text-red-800"
                    : "bg-yellow-100 text-yellow-800"
                  }`}
              >
                {selectedConference.status === "pending"
                  ? "Under Review"
                  : selectedConference.status}
              </span>
            </div>

            {/* Main content - flex layout */}
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left side - Conference Icon */}
              <div className="md:w-2/5 relative bg-gray-50 rounded-xl p-3 border border-gray-100 flex items-center justify-center min-h-[260px] md:min-h-[320px]">
                <div className="text-center">
                  <FaMicrophone className="w-24 h-24 text-yellow-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700">Paid Conference</h3>
                  <p className="text-sm text-gray-500">Paid Press Conference Request</p>
                </div>
              </div>

              {/* Right side - Details in sections */}
              <div className="md:w-3/5 flex flex-col gap-5">
                {/* Section 1: Conference Info */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                  <h3 className="text-blue-700 font-semibold mb-2 text-sm">Conference Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium">Conference ID:</span>{" "}
                      <span className="font-mono">{selectedConference.conferenceId}</span>
                    </div>
                    <div>
                      <span className="font-medium">Date:</span>{" "}
                      {selectedConference.conferenceDate}
                    </div>
                    <div>
                      <span className="font-medium">Time:</span>{" "}
                      {selectedConference.conferenceTime} {selectedConference.timePeriod}
                    </div>
                    <div>
                      <span className="font-medium">Status:</span>{" "}
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${selectedConference.status === "pending" ? "bg-yellow-100 text-yellow-800" : ""
                        }`}>
                        {selectedConference.status === "pending" ? "Under Review" : selectedConference.status}
                      </span>
                    </div>
                    <div className="sm:col-span-2">
                      <span className="font-medium">Purpose:</span>{" "}
                      <p className="mt-1 text-gray-700">{selectedConference.purpose}</p>
                    </div>
                  </div>
                </div>

                {/* Section 2: Location Details */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                  <h3 className="text-green-700 font-semibold mb-2 text-sm">Location Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium">State:</span> {selectedConference.state}
                    </div>
                    <div>
                      <span className="font-medium">City:</span> {selectedConference.city}
                    </div>
                    <div className="sm:col-span-2">
                      <span className="font-medium">Venue:</span> {selectedConference.place}
                    </div>
                    <div className="sm:col-span-2">
                      <span className="font-medium">Landmark:</span> {selectedConference.landmark || "N/A"}
                    </div>
                  </div>
                </div>

                {/* Section 3: Submitter Info */}
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                  <h3 className="text-purple-700 font-semibold mb-2 text-sm">Submitted By</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium">Name:</span> {selectedConference.submittedBy?.name || "N/A"}
                    </div>
                    <div>
                      <span className="font-medium">Organization:</span> {selectedConference.submittedBy?.organization || "N/A"}
                    </div>
                    <div>
                      <span className="font-medium">Email:</span> {selectedConference.submittedBy?.email || "N/A"}
                    </div>
                    <div>
                      <span className="font-medium">Press ID:</span> {selectedConference.submittedBy?.pressConferenceId || "N/A"}
                    </div>
                  </div>
                </div>

                {/* Section 4: Payment Information */}
                {selectedConference.paymentAmount && (
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100">
                    <h3 className="text-yellow-700 font-semibold mb-2 text-sm">Payment Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="font-medium">Amount:</span> ₹{selectedConference.paymentAmount}
                      </div>
                      <div>
                        <span className="font-medium">Payment Status:</span>
                        <span className={`ml-1 px-2 py-1 rounded-full text-xs font-semibold ${selectedConference.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                          {selectedConference.paymentStatus || 'Pending'}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Number of Reporters:</span> {selectedConference.numberOfReporters || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Payment ID:</span> {selectedConference.paymentId || 'N/A'}
                      </div>
                    </div>
                  </div>
                )}

                {/* Section 5: Admin Note */}
                {selectedConference.adminNote && (
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100">
                    <h3 className="text-yellow-700 font-semibold mb-2 text-sm">Admin Note</h3>
                    <p className="text-sm text-gray-700">{selectedConference.adminNote}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Action buttons at bottom */}
            {selectedConference.status === "pending" && (
              <div className="flex flex-col sm:flex-row justify-center gap-3 mt-8 border-t pt-5 border-gray-200">
                <button
                  onClick={() => {
                    setModal({
                      open: true,
                      action: "approve",
                      conferenceId: selectedConference.conferenceId,
                    });
                    setSelectedConference(null);
                  }}
                  className="flex items-center justify-center px-4 py-2 sm:px-6 sm:py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-semibold shadow w-full sm:w-auto"
                >
                  <FaCheck className="w-5 h-5 mr-2" />
                  Approve
                </button>
                <button
                  onClick={() => {
                    setModal({
                      open: true,
                      action: "reject",
                      conferenceId: selectedConference.conferenceId,
                    });
                    setSelectedConference(null);
                  }}
                  className="flex items-center justify-center px-4 py-2 sm:px-6 sm:py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold shadow w-full sm:w-auto"
                >
                  <FaTimes className="w-5 h-5 mr-2" />
                  Reject
                </button>
                <button
                  onClick={() => {
                    navigate("/admin/press-conference/paid-conference/modify", {
                      state: {
                        conferenceId: selectedConference.conferenceId,
                        conferenceData: selectedConference
                      }
                    });
                  }}
                  className="flex items-center justify-center px-4 py-2 sm:px-6 sm:py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-semibold shadow w-full sm:w-auto"
                >
                  <FaEdit className="w-5 h-5 mr-2" />
                  Modify
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Modal for confirm action */}
      {modal.open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 transition-all">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-[400px] max-w-full text-center relative animate-fadeIn">
            <div className="flex justify-center mb-4">
              {modal.action === "approve" ? (
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100">
                  <FaCheck className="text-green-600 text-3xl" />
                </span>
              ) : modal.action === "reject" ? (
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100">
                  <FaTimes className="text-red-600 text-3xl" />
                </span>
              ) : modal.action === "modify" ? (
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-yellow-100">
                  <FaEdit className="text-yellow-600 text-3xl" />
                </span>
              ) : null}
            </div>
            <h2 className="text-xl font-bold mb-2">
              {modal.action === "approve"
                ? "Approve Paid Conference"
                : modal.action === "reject"
                  ? "Reject Paid Conference"
                  : modal.action === "modify"
                    ? "Modify Paid Conference"
                    : ""}
            </h2>
            <p className="mb-6 text-gray-700">
              {modal.action === "approve"
                ? "Are you sure you want to approve this paid conference?"
                : modal.action === "reject"
                  ? "Are you sure you want to reject this paid conference?"
                  : modal.action === "modify"
                    ? "Are you sure you want to modify this paid conference?"
                    : ""}
            </p>

            {(modal.action === "reject" || modal.action === "modify") && (
              <div className="mb-6 text-left">
                <label
                  htmlFor="reason"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {modal.action === "reject" ? "Reason for Rejection:" : "Modification Note:"}
                </label>
                <textarea
                  id="reason"
                  value={modal.action === "reject" ? rejectReason : modifyNote}
                  onChange={(e) => modal.action === "reject" ? setRejectReason(e.target.value) : setModifyNote(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                  rows={3}
                  placeholder={modal.action === "reject" ? "Enter reason for rejection..." : "Enter modification note..."}
                />
              </div>
            )}

            {modal.action === "modify" && (
              <div className="mb-6 text-left space-y-4">
                {/* States Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select States for Reporter Matching:
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2">
                    {[
                      "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
                      "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
                      "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
                      "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
                      "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
                      "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
                      "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
                    ].map((state) => (
                      <label key={state} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          name="selectedStates"
                          value={state}
                          checked={selectedStates.includes(state)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedStates([...selectedStates, state]);
                            } else {
                              setSelectedStates(selectedStates.filter(s => s !== state));
                            }
                          }}
                          className="mr-2"
                        />
                        {state}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Cities Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Cities (Optional):
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2">
                    <input
                      type="text"
                      placeholder="Type city names separated by commas (e.g., Mumbai, Delhi, Bangalore)"
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                      value={selectedCities.join(", ")}
                      onChange={(e) => {
                        const cities = e.target.value.split(",").map(city => city.trim()).filter(city => city);
                        setSelectedCities(cities);
                      }}
                    />
                  </div>
                </div>

                {/* Reporters Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Specific Reporters (Optional):
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2">
                    {allReporters.slice(0, 10).map((reporter) => (
                      <label key={reporter._id} className="flex items-center text-sm mb-1">
                        <input
                          type="checkbox"
                          checked={selectedReporters.includes(reporter._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedReporters([...selectedReporters, reporter._id]);
                            } else {
                              setSelectedReporters(selectedReporters.filter(id => id !== reporter._id));
                            }
                          }}
                          className="mr-2"
                        />
                        {reporter.name} ({reporter.iinsafId}) - {reporter.city}, {reporter.state}
                      </label>
                    ))}
                    {allReporters.length > 10 && (
                      <p className="text-xs text-gray-500 mt-2">
                        Showing first 10 reporters. Use the modify page for full selection.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-center gap-4">
              <button
                onClick={handleModalConfirm}
                className={`px-5 py-2 rounded-lg font-semibold shadow transition-colors text-white ${modal.action === "approve"
                  ? "bg-green-500 hover:bg-green-600"
                  : modal.action === "reject"
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-yellow-500 hover:bg-yellow-600"
                  }`}
              >
                Yes
              </button>
              <button
                onClick={handleModalCancel}
                className="px-5 py-2 rounded-lg font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 shadow transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={1500} />
      {showApproveSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin" style={{ borderWidth: "4px" }} />
              {/* Middle ring */}
              <span className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slow" style={{ borderWidth: "4px", animationDuration: "2s" }} />
              {/* Inner ring */}
              <span className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slower" style={{ borderWidth: "4px", animationDuration: "3s" }} />
            </div>
            <span className="text-lg font-semibold text-blue-700 tracking-wide">
              Processing Approval...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the status.
            </span>
          </div>
        </div>
      )}
      {showRejectSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin" style={{ borderWidth: "4px" }} />
              {/* Middle ring */}
              <span className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slow" style={{ borderWidth: "4px", animationDuration: "2s" }} />
              {/* Inner ring */}
              <span className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slower" style={{ borderWidth: "4px", animationDuration: "3s" }} />
            </div>
            <span className="text-lg font-semibold text-red-700 tracking-wide">
              Processing Rejection...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the status.
            </span>
          </div>
        </div>
      )}
      {showModifySpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin" style={{ borderWidth: "4px" }} />
              {/* Middle ring */}
              <span className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slow" style={{ borderWidth: "4px", animationDuration: "2s" }} />
              {/* Inner ring */}
              <span className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slower" style={{ borderWidth: "4px", animationDuration: "3s" }} />
            </div>
            <span className="text-lg font-semibold text-yellow-700 tracking-wide">
              Processing Modification...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the status.
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default PendingPaidConference;




